const VERSION = 1;
const AAD = new TextEncoder().encode('coop-api/v1');
const encoder = new TextEncoder();
const decoder = new TextDecoder();

let publicKeyPromise;

export class CoopApiError extends Error {
  constructor(message, { status, payload, headers, cause } = {}) {
    super(message, { cause });
    this.name = 'CoopApiError';
    this.status = status;
    this.payload = payload;
    this.headers = headers;
  }
}

function apiBaseUrl() {
  return (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
}

function endpointUrl(path) {
  return `${apiBaseUrl()}${path}`;
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(value) {
  const binary = atob(value || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function pemToBytes(pem) {
  const content = String(pem || '')
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s/g, '');
  return base64ToBytes(content);
}

function makeRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function fetchPublicKey() {
  const response = await fetch(endpointUrl('/v1/pubkey'), { headers: { Accept: 'application/json' } });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.key_id || !payload?.public_key) {
    throw new CoopApiError(payload?.message || '无法获取 coop-api 公钥', { status: response.status, payload });
  }

  try {
    const key = await crypto.subtle.importKey('spki', pemToBytes(payload.public_key), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, [
      'encrypt'
    ]);
    return { keyId: payload.key_id, key };
  } catch (error) {
    throw new CoopApiError('coop-api 公钥格式无效', { cause: error });
  }
}

async function getPublicKey() {
  if (!publicKeyPromise) publicKeyPromise = fetchPublicKey();
  try {
    return await publicKeyPromise;
  } catch (error) {
    publicKeyPromise = undefined;
    throw error;
  }
}

export function clearPublicKeyCache() {
  publicKeyPromise = undefined;
}

async function encryptPayload(payload) {
  const { keyId, key } = await getPublicKey();
  const rawAesKey = crypto.getRandomValues(new Uint8Array(32));
  const aesKey = await crypto.subtle.importKey('raw', rawAesKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const requestId = makeRequestId();
  const encryptedKey = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, rawAesKey);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: AAD, tagLength: 128 },
    aesKey,
    encoder.encode(JSON.stringify(payload))
  );

  return {
    aesKey,
    requestId,
    envelope: {
      version: VERSION,
      key_id: keyId,
      request_id: requestId,
      timestamp: Math.floor(Date.now() / 1000),
      encrypted_key: bytesToBase64(new Uint8Array(encryptedKey)),
      nonce: bytesToBase64(nonce),
      ciphertext: bytesToBase64(new Uint8Array(ciphertext))
    }
  };
}

function responseBody(headers, body, responseType) {
  const bytes = base64ToBytes(body || '');
  const text = decoder.decode(bytes);
  if (responseType === 'text') return text;
  if (!text) return null;

  const contentType = String(headers?.['Content-Type'] || headers?.['content-type'] || '').toLowerCase();
  if (contentType.includes('application/json') || /^[\[{]/.test(text.trim())) {
    try {
      return JSON.parse(text);
    } catch {
      // 上游声明为 JSON 但响应体不合法时，保留原始文本方便定位问题。
    }
  }
  return text;
}

function errorMessage(payload, fallback) {
  if (typeof payload === 'string') return payload || fallback;
  return payload?.message || payload?.msg || fallback;
}

/**
 * 调用 coop-api 的加密信封接口并解密其内层响应。
 * 返回值是接口内层 body，不泄漏 admanager Token 到浏览器。
 */
export async function encryptedRequest(endpoint, payload, { signal, responseType = 'json' } = {}) {
  if (!globalThis.crypto?.subtle) {
    throw new CoopApiError('当前浏览器不支持 Web Crypto，无法建立加密 API 连接');
  }

  const { aesKey, requestId, envelope } = await encryptPayload(payload);
  let response;
  let responseEnvelope;
  try {
    response = await fetch(endpointUrl(endpoint), {
      method: 'POST',
      signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify(envelope)
    });
    responseEnvelope = await response.json().catch(() => null);
  } catch (error) {
    // 请求被外部 signal 终结（重复请求去重/手动取消）：抛可区分的取消错误，调用方按取消处理而非失败。
    // name 保留 AbortError 以便兼容现有按 err.name 判断的调用方（如 MaterialManagerPage）。
    if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
      const cancelError = new CoopApiError('请求已被取消', { cause: error });
      cancelError.isCancel = true;
      cancelError.canceled = true;
      cancelError.name = 'AbortError';
      throw cancelError;
    }
    throw new CoopApiError('无法连接 coop-api', { cause: error });
  }

  if (!response.ok) {
    throw new CoopApiError(responseEnvelope?.message || response.statusText || 'coop-api 请求失败', {
      status: response.status,
      payload: responseEnvelope
    });
  }
  if (!responseEnvelope?.ciphertext || !responseEnvelope?.nonce) {
    throw new CoopApiError('coop-api 返回了无效的加密响应', { status: response.status, payload: responseEnvelope });
  }
  if (responseEnvelope.request_id !== requestId) {
    throw new CoopApiError('coop-api 响应 request_id 不匹配');
  }

  let plain;
  try {
    plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(responseEnvelope.nonce), additionalData: AAD, tagLength: 128 },
      aesKey,
      base64ToBytes(responseEnvelope.ciphertext)
    );
  } catch (error) {
    throw new CoopApiError('无法解密 coop-api 响应', { cause: error });
  }

  let inner;
  try {
    inner = JSON.parse(decoder.decode(plain));
  } catch (error) {
    throw new CoopApiError('coop-api 响应内容格式无效', { cause: error });
  }

  const body = responseBody(inner.headers, inner.body, responseType);
  if (!Number.isInteger(inner.status) || inner.status < 200 || inner.status >= 300) {
    throw new CoopApiError(errorMessage(body, `请求失败（${inner.status || 'unknown'}）`), {
      status: inner.status,
      payload: body,
      headers: inner.headers
    });
  }
  return { body, status: inner.status, headers: inner.headers || {} };
}

export async function authRequest(action, data, options) {
  const result = await encryptedRequest(`/v1/auth/${action}`, data, options);
  return result.body;
}
